import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LifeBuoy, Loader2 } from "lucide-react";

const STATUSES = ["nouveau", "en_cours", "repondu", "ferme"];
const STATUS_FR = {
  nouveau: { label: "Nouveau", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  en_cours: { label: "En cours", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  repondu: { label: "Répondu", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ferme: { label: "Fermé", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function AdminSupport() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [replies, setReplies] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/support", { params: filter ? { status: filter } : {} });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const update = async (id, body) => {
    setBusy(id);
    try {
      await api.patch(`/admin/support/${id}`, body);
      toast.success("Ticket mis à jour.");
      setReplies((r) => ({ ...r, [id]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-support-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
        <LifeBuoy className="text-[#E05638]" size={26} /> Support partenaires
      </h1>
      <div className="mt-4 flex gap-2">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-full text-sm border ${!filter ? "bg-slate-900 text-white" : "bg-white"}`} data-testid="admin-support-filter-all">Tous</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-sm border ${filter === s ? "bg-slate-900 text-white" : "bg-white"}`} data-testid={`admin-support-filter-${s}`}>
            {STATUS_FR[s].label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="p-8 text-center text-slate-400 flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-8 text-center text-slate-500 text-sm">Aucun ticket.</div>
        ) : items.map((t) => {
          const st = STATUS_FR[t.status] || { label: t.status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
          return (
            <div key={t.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`admin-support-ticket-${t.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-slate-900">{t.subject}</div>
                  <div className="text-xs text-slate-400">{t.business_name} · {t.owner_email} · {(t.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${st.cls}`}>{st.label}</span>
              </div>
              <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{t.message}</p>
              {t.reply && <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-sm text-slate-700"><b className="text-emerald-700 text-xs">Votre réponse :</b> {t.reply}</div>}
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <textarea value={replies[t.id] || ""} onChange={(e) => setReplies((r) => ({ ...r, [t.id]: e.target.value }))}
                          rows={2} placeholder="Répondre au partenaire…" className="flex-1 rounded-md border border-slate-200 p-2 text-sm" data-testid={`admin-support-reply-${t.id}`} />
                <div className="flex gap-2">
                  <Button size="sm" disabled={busy === t.id || !(replies[t.id] || "").trim()} onClick={() => update(t.id, { reply: replies[t.id] })} className="bg-[#E05638] hover:bg-[#c94a2e]" data-testid={`admin-support-send-${t.id}`}>Répondre</Button>
                  <Button size="sm" variant="outline" disabled={busy === t.id} onClick={() => update(t.id, { status: "en_cours" })}>En cours</Button>
                  <Button size="sm" variant="outline" disabled={busy === t.id} onClick={() => update(t.id, { status: "ferme" })}>Fermer</Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
